import { useLiveQuery } from 'dexie-react-hooks';
import { profileProblems } from '@/engine/profile';
import { buildTargets } from '@/engine/targets';
import type { Profile, Targets } from '@/engine/types';
import { db, nowIso, putAndQueue, type ProfileRow } from '@/lib/db';
import { todayIso } from '@/lib/format';

export const PROFILE_ID = 'me';

/** Valores de partida del onboarding. Nada aquí se guarda hasta que el usuario acepta. */
export const DEFAULT_PROFILE: Profile = {
  sex: 'hombre',
  birthDate: '1995-01-01',
  heightCm: 175,
  weightKg: 75,
  activity: 'moderado',
  goal: 'mantener',
  pace: 'normal',
  proteinPerKg: 1.8,
  vegetarian: false,
  allergies: [],
  dislikes: [],
  displayUnit: 'kg',
  hideCalories: false,
};

export type ProfileState =
  | { status: 'cargando' }
  | { status: 'sin_perfil' }
  /** Guardado antes de validarse, o llegado así de otro dispositivo: se pide corregir. */
  | { status: 'invalido'; profile: Profile; problems: string[] }
  | { status: 'listo'; profile: Profile; targets: Targets };

export function useProfileState(): ProfileState {
  // `?? null` para distinguir "aún consultando" de "no hay perfil": sin esto la
  // app se queda en la pantalla de espera para siempre.
  const row = useLiveQuery(async () => (await db.profile.get(PROFILE_ID)) ?? null, []);

  if (row === undefined) return { status: 'cargando' };
  if (row === null || row.deletedAt !== null) return { status: 'sin_perfil' };

  const profile = stripSyncFields(row);
  // `buildTargets` lanza con datos imposibles, y aquí se está pintando: sin
  // esta puerta, un peso a 0 dejaba la app en blanco para siempre.
  const problems = profileProblems(profile, todayIso());
  if (problems.length > 0) return { status: 'invalido', profile, problems };
  return { status: 'listo', profile, targets: buildTargets(profile, todayIso()) };
}

function stripSyncFields(row: ProfileRow): Profile {
  const { updatedAt: _u, deletedAt: _d, id: _i, ...profile } = row;
  return profile;
}

/**
 * Única puerta de escritura del perfil: lo que no se puede calcular no se
 * guarda (ni se sube a la nube). Lanza con las frases de `profileProblems`.
 */
export async function saveProfile(profile: Profile): Promise<void> {
  const problems = profileProblems(profile, todayIso());
  if (problems.length > 0) throw new Error(problems.join(' '));

  const row: ProfileRow = {
    ...profile,
    id: PROFILE_ID,
    updatedAt: nowIso(),
    deletedAt: null,
  };
  await putAndQueue('profile', PROFILE_ID, row);
}
